/**
 * Krok UXR1 — sloupec „Poslední změna" a casy v „Zakázkách v pohybu" podle
 * mockupu `02-zakazky.html` / `01-dnesek.html`: „dnes 9:40", „včera 16:40",
 * „29. 9.". Makler nepotrebuje presne datum u veci, ktera se stala pred
 * hodinou, a u mesic stare zakazky nepotrebuje minuty.
 *
 * Funkce je ciste vypocetni (zadne `new Date()` uvnitr, zadne texty uvnitr) —
 * „ted" i popisky dodava volajici, aby sla testovat bez posunu casu a aby
 * popisky mohly jit pres `t()` (CLAUDE.md §5: zadny natvrdo psany text).
 */

export interface RelativeTimeTexts {
  /** napr. `(time) => \`dnes ${time}\`` */
  readonly today: (time: string) => string;
  readonly yesterday: (time: string) => string;
}

/**
 * Krok UXR5 bod 5 — datum nad pozdravem s VELKYM pismenem dne („Pátek 2.
 * října 2026", predloha `Main.dc.html`). `Intl` vraci nazev dne malym
 * pismenem, protoze cestina ho uprostred vety tak pise; tady ale stoji na
 * zacatku radku. Velke pismeno proto nese formatovani, ne preklad — jinak by
 * se nazvy dnu musely opsat do katalogu a rozesly by se s `Intl`.
 */
export function formatFullDate(date: Date, locale = 'cs-CZ'): string {
  const text = new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).format(date);
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

/**
 * `iso` → „dnes 9:40" / „včera 16:40" / „29. 9." / „29. 9. 2025".
 * Neplatny vstup vraci puvodni retezec, ne „Invalid Date".
 */
export function formatRelativeChange(
  iso: string,
  now: Date,
  texts: RelativeTimeTexts,
  locale = 'cs-CZ',
): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(at);
  if (isSameDay(at, now)) return texts.today(time);

  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (isSameDay(at, yesterday)) return texts.yesterday(time);

  const sameYear = at.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(at);
}
